// Bobbie Partner Theme - JavaScript color definitions
// These colors should match the CSS variables in _theme-bobbie.scss

export const bobbieTheme = {
  // Font families
  fontPrimary: '"Manrope", sans-serif',
  fontSecondary: '"Roboto", sans-serif',

  // Primary brand colors
  primary: "#005387",
  secondary: "#D6D5CB",
  accent: "#0149f5",
  dark: "#002634",

  // Semantic colors
  positive: "#237F52",
  negative: "#FF2D21",
  info: "#002634",
  warning: "#F9A900",

  // Grey scale
  black: "#000000",
  white: "#ffffff",
  grey50: "#fafafa",
  grey100: "#f5f5f5",
  grey200: "#eeeeee",
  grey300: "#e0e0e0",
  grey400: "#bdbdbd",
  grey500: "#9e9e9e",
  grey600: "#757575",
  grey700: "#616161",
  grey800: "#424242",
  grey900: "#212121",

  // Grey-blue (GREB) scale
  greb50: "#fafafa",
  greb100: "#f0f3f4",
  greb200: "#dbe2e4",
  greb300: "#b8c6cc",
  greb400: "#8c9fa7",
  greb500: "#6c828b",
  greb600: "#4e6169",
  greb700: "#405056",
  greb800: "#283135",
  greb900: "#171a1b",

  // Emerald (EMER) scale - Bobbie uses blue tones
  emer50: "#E6F5FF",
  emer100: "#B8E4FF",
  emer200: "#8AD2FF",
  emer300: "#2EAFFF",
  emer400: "#009DFF",
  emer500: "#0081D1",
  emer600: "#0065A3",
  emer700: "#005387",
  emer800: "#002C47",
  emer900: "#00101A",

  // Honey scale
  honey50: "#FFF7E6",
  honey100: "#FFE8B8",
  honey200: "#FFDA8A",
  honey300: "#FFCB5C",
  honey400: "#FFBD2E",
  honey500: "#F9A900",
  honey600: "#D18F00",
  honey700: "#A37000",
  honey800: "#755000",
  honey900: "#473100",

  // Green (GREE) scale - Bobbie uses blue tones
  gree100: "#5D8DFE",
  gree200: "#0149f5",
  gree300: "#2EAFFF",
  gree400: "#012375",

  // Yellow (YELL) scale
  yell100: "#ffeba4",
  yell200: "#ffdd64",
  yell300: "#ffc73d",
  yell400: "#edac25",

  // Danger (DANG) scale
  dang500: "#fffdfe",
  dang600: "#FF908A",
  dang700: "#FF2D21",
  dang800: "#A30800",
  dang900: "#510f17",

  // Success (SUCC) scale
  succ500: "#fdfffd",
  succ600: "#37C882",
  succ700: "#237F52",
  succ800: "#195C3C",
  succ900: "#1e2d0c",

  // Warning (WARN) scale
  warn500: "#fefdfd",
  warn600: "#FFBD2E",
  warn700: "#F9A900",
  warn800: "#A37000",
  warn900: "#531907",

  // Element Plus / UI colors
  elWarning: "#F9A900",
  elSuccess: "#237F52",
  elDanger: "#FF2D21",
  elInfo: "#909399",
  elPrimary: "#5D8DFE",

  // Additional colors used in the app
  alertRed: "#FF2D21",
  badgePink: "#A30800",
  maintenanceBg: "#303742",
  mapGreen: "#0149f5",
  flyingGuideInactive: "#0149f5",
  flyingGuideActive: "#1377d5",
  warnOrange: "#ff7f37",
  linkBlue: "#0275d8",

  // Classification colors
  classS: "#808080",
  classM: "#eab144",
  classL: "#0149f5",
  classXl: "#005387",

  // UI Element colors
  elBorder: "#dcdfe6",
  rtkBadge: "#99e4ff",
  inactiveText: "#97979763",
  lightBg: "#f8f9fa",
  lightBorder: "#e9ecef",
  buttonHoverBg: "#e1e5e8",
  linkPrimary: "#3d58db",
  primaryDark: "#0131A2",
  primaryLightBg: "#f8fbfb",

  // Dark theme colors
  darkBgPrimary: "#27292c",
  darkBgSecondary: "#333537",
  darkBgTertiary: "#3b3c3d",
  darkBgQuaternary: "#404143",
  darkBgElevated: "#505255",
  darkBgBlack: "#1a1c1e",

  // Additional UI colors
  borderLight: "#e1e1e1",
  borderDark: "#c0c0c038",
  borderSeparator: "#4c4c4c",
  borderDivider: "#7f7777",

  textMuted: "#7d7d7d",
  textLight: "#d8d8d8",
  textSubtle: "#666",
  textPlaceholder: "#aaaaaa",
  textDark: "#333",

  errorRed: "#FF2D21",
  disabled: "#a0a0a0",
  hoverBg: "#707070",

  // 3D Viewer specific colors - using Bobbie blue tones
  viewerHighlight: "#8AD2FF",
  viewerGreenLight: "#009DFF",
  viewerGreenDark: "#0149f5",
  viewerGreenDarker: "#005387",

  // 3D Viewer semantic color tokens (read by viewer-colors.js / initViewerColors)
  viewerPointColor: "#2EAFFF",            // POINT_COLOR — main interactive point (blue)
  viewerSelectedPointColor: "#2EAFFF",    // SELECTED_POINT_COLOR (dark blue)
  viewerMidpointColor: "#2EAFFF",         // MIDPOINT_COLOR (light blue)
  viewerMeasurementPointColor: "#2EAFFF", // MEASUREMENT_POINT_COLOR (very dark blue)
  viewerRestrictedAreaColor: "#FF2D21",   // RESTRICTED_AREA_COLOR
  viewerSelectedLineColor: "#ffdd79",     // SELECTED_LINE_COLOR (keep warm yellow for contrast)
  viewerOutlineColor: "#fff5d4",          // OUTLINR_COLOR (panel outline)

  // Mode specific colors
  modeArea: "#c4724c",
  modeMeasure: "#1557b5",
  modeSolar: "#0149f5",
  modeInactive: "#aba9aa",
  modeRestricted: "#A30800",

  // Tailwind-like colors
  blue500: "#3b82f6",
  blue600: "#2563eb",
  green500: "#22c55e",
  green600: "#16a34a",
  red400: "#f87171",
  red500: "#ef4444",

  // Additional form/input colors
  inputBorder: "#dddddd",
  inputBorderLight: "#d9d9d9",
  dashedBorder: "#4488ee",
  paginationBorder: "#ddd",
  dottedBorder: "#d8d8d8",
  textSecondary: "#7e7e7e",
  textMedium: "#636c71",
  textTertiary: "#71797d",
  textDim: "#464a4c",
  borderGrey: "#e3e3e3",
  borderWhiteAlpha: "#ffffff52",

  // Additional accent colors
  lila: "#a385e2",
  gold: "#ffd700",
};
